import { MAX_OPEN_SHIFT_HOURS } from './clockService.js';
import type { TrustedEntry, TrustedPersonRecord } from './domain.js';
import { businessDayOf } from './domain.js';

// « Hors Staff » presence (ADR-0024) — pure. The point of the feature is to know
// WHO IS ON THE PREMISES, so this answers that and nothing payroll-shaped:
// there is deliberately no hours total here.

/**
 * Who is in right now: the person's most recent punch has no clock-out AND was
 * opened within MAX_OPEN_SHIFT_HOURS. An older open punch is a forgotten
 * clock-out (the kiosk applies the same rule when someone punches again), so it
 * is reported by `forgottenOut` instead of pretending the person is still there.
 */
export function presentNow(
  entries: TrustedEntry[],
  nowMs: number,
): { present: TrustedEntry[]; forgottenOut: TrustedEntry[] } {
  const latest = new Map<string, TrustedEntry>();
  for (const e of entries) {
    const cur = latest.get(e.personId);
    if (!cur || e.clockIn > cur.clockIn) latest.set(e.personId, e);
  }
  const present: TrustedEntry[] = [];
  const forgottenOut: TrustedEntry[] = [];
  for (const e of [...latest.values()].sort((a, b) => a.clockIn.localeCompare(b.clockIn))) {
    if (e.clockOut !== null) continue;
    const ageMs = nowMs - Date.parse(e.clockIn);
    (ageMs > MAX_OPEN_SHIFT_HOURS * 3_600_000 ? forgottenOut : present).push(e);
  }
  return { present, forgottenOut };
}

/** « Prénom Nom », or the nickname in quotes after it when there is one. */
export function trustedPersonLabel(p: Pick<TrustedPersonRecord, 'firstName' | 'lastName' | 'nickname'>): string {
  const base = `${p.firstName} ${p.lastName}`.trim();
  return p.nickname ? `${base} « ${p.nickname} »` : base;
}

/**
 * The people the kiosk's « Aujourd'hui » chart can name: every employee, plus the
 * « Hors Staff » people who have PUNCHED today. A trusted person who has not
 * punched is left out on purpose — the chart's « Pas de pointage » group means
 * « staff who should have punched », which does not apply to a volunteer.
 * Without this the chart cannot resolve their punch and labels it by id.
 */
export function chartPeople<P extends { id: string }>(
  employees: P[],
  trustedPersons: P[],
  todaysEntries: Array<{ employeeId: string }>,
): P[] {
  const punched = new Set(todaysEntries.map((e) => e.employeeId));
  return [...employees, ...trustedPersons.filter((p) => punched.has(p.id))];
}

/** One person's presence over a period — for the printed « Relevé de présence ». */
export interface PresenceReport {
  fromDay: string;
  toDay: string;
  /** Only days with at least one punch, oldest first — a volunteer is not « owed » days, so there are no « repos » rows. */
  days: Array<{ day: string; shifts: TrustedEntry[]; totalHours: number }>;
  daysPresent: number;
  /** Number of arrivals (a day with two visits counts twice). */
  visits: number;
  /** Time on the premises over the period, in decimal hours. Open punches add nothing (see `openCount`). */
  totalHours: number;
  /** Punches with no departure — to be corrected before the report is relied on. */
  openCount: number;
}

/**
 * Decimal hours between arrival and departure; null while the punch has no
 * departure (a forgotten clock-out is never guessed at, and never counted).
 */
export function entryHours(e: Pick<TrustedEntry, 'clockIn' | 'clockOut'>): number | null {
  if (e.clockOut === null) return null;
  const ms = Date.parse(e.clockOut) - Date.parse(e.clockIn);
  return ms > 0 ? ms / 3_600_000 : 0;
}

/**
 * Group one person's punches into business days within [fromDay, toDay]
 * (inclusive), with the time on the premises per day and for the period.
 * This is PRESENCE hours: no contract, no overtime, no comparison with a plan
 * (ADR-0024) — it is not a timesheet and carries no payroll meaning.
 */
export function presenceReport(
  entries: TrustedEntry[],
  personId: string,
  fromDay: string,
  toDay: string,
): PresenceReport {
  const byDay = new Map<string, TrustedEntry[]>();
  for (const e of entries) {
    if (e.personId !== personId) continue;
    const day = businessDayOf(new Date(e.clockIn));
    if (day < fromDay || day > toDay) continue;
    const list = byDay.get(day);
    if (list) list.push(e);
    else byDay.set(day, [e]);
  }
  const days = [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, shifts]) => {
      const sorted = shifts.sort((a, b) => a.clockIn.localeCompare(b.clockIn));
      return { day, shifts: sorted, totalHours: sorted.reduce((h, s) => h + (entryHours(s) ?? 0), 0) };
    });
  return {
    fromDay,
    toDay,
    days,
    daysPresent: days.length,
    visits: days.reduce((n, d) => n + d.shifts.length, 0),
    totalHours: days.reduce((h, d) => h + d.totalHours, 0),
    openCount: days.reduce((n, d) => n + d.shifts.filter((s) => s.clockOut === null).length, 0),
  };
}
